import React, { useCallback, useEffect, useMemo, useState } from 'react';

import GridWrapper from '../../shared/components/layout/GridWrapper';
import Button from '../../shared/components/ui/Button';
import Modal from '../../shared/components/ui/Modal';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';

import company_image from '../../shared/assets/png/company_image.png';
import ProjectOverview from '../components/layout/ProjectOverview';
import AccommodationDetails from './AccommodationDetails';
import ProjectManagementTasks from './Management/ProjectManagementTasks';
import { AddAccommodation } from '../components/forms/AddAccommodation';
import PreviewBlockDetails from '../components/layout/PreviewBlockDetails';
import PreviewUnitDetails from '../components/layout/PreviewUnitDetails';
import { RateList } from '../../builder/pages/RateList';
import { ImportAccommodation } from '../components/forms/ImportAccommodation';
import ImportReraModal from '../components/forms/ImportReraModal';
import accommodationData from '../static/data/accommodation.json';
import {
  createAccommodation,
  deleteProject,
  editProject,
  editAccommodation,
  getAllAccommodations,
  getProjectByID,
  getAllAccommodationsByProject,
  handleCheckProjectDelete
} from '../services/api/implementation/impl';
import { EditAccommodation } from '../components/forms/EditAccommodation';
import EditProject from '../components/forms/EditProject';
import { PLCsDetails } from './PLCsDetails';
import { BlockDetails } from './BlockDetails';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSmartBack } from '../../shared/navigation';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { transformPayload } from '../utils/transformPayload';
import PaymentStagesDetails from './PaymentStagesDetails';
import { useSelector } from 'react-redux';
import UnitPricingList from './UnitPricingList';
import { Document, ProjectDocument } from './ProjectDocument';
import Cookies from 'js-cookie';
import PropertyDocuments from './PropertyDocuments';
import ProjectLayoutTab from '../components/layout/ProjectLayoutTab';
import { ExtraChargesList } from './ExtraChargesList';
import DirectionsToUnits from './DirectionsToUnits';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import Headroom from 'react-headroom';
import mixpanel from 'mixpanel-browser';
import DeleteConfirmationModal from '../components/ui/DeleteConfirmationModal';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import Loader from '../../shared/components/ui/Loader';
import { PROJECT_SETUP_TYPES } from '../utils/projectStructurePriorities';
import { triggerProjectStructureReview } from '../utils/triggerProjectStructureReview';

const ProjectDetails = () => {
  const userId = Cookies.get('userId');
  const navigate = useNavigate();
  const location = useLocation();
  const { id: projectId } = useParams();
  const { projects, refetchProjects } = useProjectsList();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileUrls = useSelector((state) => state.file.fileUrls);
  const { collapsed } = useSidebar();

  console.log({ fileUrls });

  /* 
    Data fetching
  */
  const {
    data: project,
    refetch: refetchProject,
    isLoading: isProjectLoading,
    isFetching: isProjectFetching
  } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProjectByID(projectId)
  });

  const companyId = project?.company_id ?? project?.company?.id;
  const companyIdInt = parseInt(companyId, 10);
  const { onBack } = useSmartBack({
    fallback:
      companyId != null && companyId !== ''
        ? `/builder/management/company/${companyId}/projects`
        : '/builder/management'
  });
  const projectsForDeleteModal = useMemo(() => {
    if (companyId == null || Number.isNaN(companyIdInt)) return [];
    return (projects || []).filter((p) => Number(p.company_id) === companyIdInt);
  }, [projects, companyId, companyIdInt]);

  //store project.measurement_type in cookies whever it

  // Track page view
  useEffect(() => {
    mixpanel.track('Project Details Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    Cookies.set('measurement_type', project?.measurement_type);
  }, [project]);

  // const { data: accommodations, refetch: refetchAccommodations } = useQuery({
  //   queryKey: ['accommodations'],
  //   queryFn: getAllAccommodations
  // });

  const {
    data: accommodations,
    refetch: refetchAccommodations,
    isLoading: isAccommodationsLoading,
    isFetching: isAccommodationsFetching,
    isError: isAccommodationsError,
    error: accommodationsError
  } = useQuery({
    queryKey: ['accommodations', projectId],
    queryFn: () => getAllAccommodationsByProject(projectId)
  });

  const formatIndependent = [
    { type: 'float', label: 'width' },
    { type: 'float', label: 'length' },
    { type: 'float', label: 'plotArea' },
    { type: 'float', label: 'buildUpArea' },
    { type: 'float', label: 'buildUpArea' },
    { type: 'float', label: 'carpetArea' },
    { type: 'integer', label: 'projectId' }
  ];

  const formatGroup = [
    { type: 'float', label: 'width' },
    { type: 'float', label: 'length' },
    { type: 'float', label: 'plotArea' },
    { type: 'float', label: 'buildUpArea' },
    { type: 'float', label: 'buildUpArea' },
    { type: 'float', label: 'carpetArea' },
    { type: 'float', label: 'loadingFactor' },
    { type: 'integer', label: 'projectId' }
  ];

  const calculateSuperBuildUpAreaValue = (buildUpArea, loadingFactor) => {
    const area = parseFloat(buildUpArea) || 0;
    const factor = parseFloat(loadingFactor) || 0;
    if (!area) return '0';
    if (!factor) return String(area);
    return String(Math.round(area * (1 + factor / 100) * 100) / 100);
  };

  const resolveSuperBuildUpArea = (payload) => {
    if (payload.superBuildUpArea !== undefined && payload.superBuildUpArea !== '') {
      return String(payload.superBuildUpArea);
    }
    return calculateSuperBuildUpAreaValue(payload.buildUpArea, payload.loadingFactor);
  };

  const formatParking = [
    { type: 'float', label: 'buildUpArea' },
    { type: 'boolean', label: 'electrified' },
    { type: 'integer', label: 'projectId' }
  ];

  /* 
    Data mutation
  */
  const { mutate: handleOnSubmitIndependent, isLoading: isAddingIndependent } = useMutation({
    mutationFn: (payload) =>
      createAccommodation(
        transformPayload(
          {
            ...payload,
            buildUpArea: payload.buildUpArea || 0,
            carpetArea: payload.carpetArea || 0,
            floorPlanLink: fileUrls?.floorPlanLink,
            unitSpecification: fileUrls?.unitSpecification,
            accomodationType: 'INDEPENDENT',
            materialListLink: fileUrls?.materialListLink,
            projectId
          },
          formatIndependent
        )
      ),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Accommodation created successfully');
      setOpenAccommodation(false);
      await refetchAccommodations(); // UI data update
      await triggerProjectStructureReview(projectId, PROJECT_SETUP_TYPES.ACCOMMODATION);
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error creating new accommodation');
      toast.error(err.response.data.message || err.message || 'Error creating new accommodation');
    }
  });

  const { mutate: handleOnSubmitGroup, isLoading: isAddingGroup } = useMutation({
    mutationFn: (payload) =>
      createAccommodation(
        transformPayload(
          {
            ...payload,
            superBuildUpArea: resolveSuperBuildUpArea(payload),
            floorPlanLink: fileUrls?.floorPlanLink,
            unitSpecification: fileUrls?.unitSpecification,
            accomodationType: 'GROUP',
            materialListLink: fileUrls?.materialListLink,
            projectId
          },
          formatGroup
        )
      ),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Accommodation created successfully');
      setOpenAccommodation(false);
      await refetchAccommodations(); // UI data update
      await triggerProjectStructureReview(projectId, PROJECT_SETUP_TYPES.ACCOMMODATION);
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error creating new accommodation');
      toast.error(err.response.data.message || err.message || 'Error creating new accommodation');
    }
  });

  const { mutate: handleOnSubmitParking, isLoading: isAddingParking } = useMutation({
    mutationFn: (payload) =>
      createAccommodation(
        transformPayload(
          {
            ...payload,
            accomodationType: 'PARKING',
            projectId
          },
          formatParking
        )
      ),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Accommodation created successfully');
      setOpenAccommodation(false);
      await refetchAccommodations(); // UI data update
      await triggerProjectStructureReview(projectId, PROJECT_SETUP_TYPES.ACCOMMODATION);
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error creating new accommodation');
      toast.error(err.response.data.message || err.message || 'Error creating new accommodation');
    }
  });

  // Edit independent

  const { mutate: handleOnEditSubmitIndependent, isLoading: isEditIndependent } = useMutation({
    mutationFn: (payload) =>
      editAccommodation(
        StaticId,
        transformPayload(
          {
            ...payload,
            buildUpArea: payload.buildUpArea || 0,
            carpetArea: payload.carpetArea || 0,
            floorPlanLink: fileUrls?.floorPlanLink,
            unitSpecification: fileUrls?.unitSpecification,
            accomodationType: 'INDEPENDENT',
            materialListLink: fileUrls?.materialListLink,
            projectId
          },
          formatIndependent
        )
      ),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Accommodation Updated successfully');
      setIsOpenEditAccommodation(false);
      await refetchAccommodations(); // UI data update
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error creating new accommodation');
      toast.error(err.response.data.message || err.message || 'Error creating new accommodation');
    }
  });

  // Edit Group

  const { mutate: handleOnEditSubmitGroup, isLoading: isAddingEditGroup } = useMutation({
    mutationFn: (payload) =>
      editAccommodation(
        StaticId,
        transformPayload(
          {
            ...payload,
            superBuildUpArea: resolveSuperBuildUpArea(payload),
            floorPlanLink: fileUrls?.floorPlanLink,
            unitSpecification: fileUrls?.unitSpecification,
            accomodationType: 'GROUP',
            materialListLink: fileUrls?.materialListLink,
            projectId
          },
          formatGroup
        )
      ),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Accommodation Update Group successfully');
      setIsOpenEditAccommodation(false);
      await refetchAccommodations(); // UI data update
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error creating new accommodation');
      toast.error(err.response.data.message || err.message || 'Error creating new accommodation');
    }
  });

  // Edit Parking
  const { mutate: handleOnEditSubmitParking, isLoading: isAddingEditParking } = useMutation({
    mutationFn: (payload) =>
      editAccommodation(
        StaticId,
        transformPayload(
          {
            ...payload,
            accomodationType: 'PARKING',
            projectId
          },
          formatParking
        )
      ),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Accommodation created successfully');
      setIsOpenEditAccommodation(false);
      await refetchAccommodations(); // UI data update
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error creating new accommodation');
      toast.error(err.response.data.message || err.message || 'Error creating new accommodation');
    }
  });

  /*
    Hooks 
  */
  const independentData = accommodationData.filter(
    (item) => item.accommodation_type === 'Independent'
  );
  const groupData = accommodationData.filter((item) => item.accommodation_type === 'Group');
  const parkingData = accommodationData.filter((item) => item.accommodation_type === 'Parking');
  /* 
    Local state 
  */
  const [secondaryTab, setSecondaryTab] = useState('Overview');
  const [isOpenEditProject, setIsOpenEditProject] = useState(false);
  const [upShift, setUpShift] = useState(6);
  const [OpenAccommodation, setOpenAccommodation] = useState(false);
  const [isOpenAddDetailsModal, setIsOpenAddDetailsModal] = useState(false);
  const [isOpenBlockDetailingModal, setIsOpenBlockDetailingModal] = useState(false);
  const [isOpenUnitDetailingModal, setIsOpenUnitDetailingModal] = useState(false);
  const [isOpenImportAccommodation, setisOpenImportAccommodation] = useState(false);
  const [isOpenImportRera, setIsOpenImportRera] = useState(false);
  const [isOpenEditAccommodation, setIsOpenEditAccommodation] = useState(false);
  const [isShow, setIsShow] = useState('INDEPENDENT');
  const [AddIndependentStaticState, setAddIndependentStaticState] = useState(independentData);
  const [AddGroupStaticState, setAddGroupStaticState] = useState(groupData);
  const [AddParkingStaticState, setAddParkingStaticState] = useState(parkingData);
  const [StaticId, setStaticId] = useState();
  const [canDelete, setCanDelete] = useState(null);
  const [resData, setResData] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [isProjectDeleteBusy, setIsProjectDeleteBusy] = useState(false);

  /* 
    Meta data
  */
  const secondaryTabbedComponentMetaData = {
    tabs: [
      'Overview',
      'Management Task',
      'Accommodation',
      'Block',
      'Pricing',
      'Layout',
      'Property Templates',
      'Project Documents'
    ]
  };

  // Pricing sub-tabs (second-level tabs when Pricing is selected)
  const pricingSubTabs = [
    'Unit Pricing',
    'Accommodation Pricing',
    'PLC',
    'Extra Charges',
    'Directions to Units',
    'Payment Schedule'
  ];
  const paymentScheduleSubTabs = ['Independent', 'Group', 'Templates'];
  const [pricingSubTab, setPricingSubTab] = useState('Unit Pricing');
  const [paymentScheduleSubTab, setPaymentScheduleSubTab] = useState('Independent');

  const [unitPricingBookPortalEl, setUnitPricingBookPortalEl] = useState(null);
  const unitPricingBookPortalRef = useCallback((node) => {
    setUnitPricingBookPortalEl(node);
  }, []);

  const [accommodationPricingFilterPortalEl, setAccommodationPricingFilterPortalEl] =
    useState(null);
  const accommodationPricingFilterPortalRef = useCallback((node) => {
    setAccommodationPricingFilterPortalEl(node);
  }, []);

  const handleToggleEditProjectModal = useCallback(() => {
    setIsOpenEditProject((prev) => !prev);
  }, []);

  useEffect(() => {
    const savedPricingSubTab = location.state?.pricingSubTab;
    const savedSecondaryTab =
      location.state?.secondaryTab || localStorage.getItem('secondaryTab');
    const savedPaymentScheduleView = localStorage.getItem('pricingPaymentScheduleSubTab');

    if (savedPaymentScheduleView && paymentScheduleSubTabs.includes(savedPaymentScheduleView)) {
      setPaymentScheduleSubTab(savedPaymentScheduleView);
    }

    // Priority redirects: main tab Pricing + pricing sub-tab (e.g. Accommodation Pricing)
    if (savedPricingSubTab && pricingSubTabs.includes(savedPricingSubTab)) {
      setSecondaryTab('Pricing');
      setPricingSubTab(savedPricingSubTab);
      localStorage.setItem('secondaryTab', savedPricingSubTab);
      return;
    }

    if (savedSecondaryTab) {
      if (paymentScheduleSubTabs.includes(savedSecondaryTab)) {
        setSecondaryTab('Pricing');
        setPricingSubTab('Payment Schedule');
        setPaymentScheduleSubTab(savedSecondaryTab);
        localStorage.setItem('secondaryTab', 'Payment Schedule');
        localStorage.setItem('pricingPaymentScheduleSubTab', savedSecondaryTab);
      } else if (savedSecondaryTab === 'Payment Schedule') {
        setSecondaryTab('Pricing');
        setPricingSubTab('Payment Schedule');
      } else if (pricingSubTabs.includes(savedSecondaryTab)) {
        setSecondaryTab('Pricing');
        setPricingSubTab(savedSecondaryTab);
      } else if (savedSecondaryTab === `PLC's`) {
        setSecondaryTab('Pricing');
        setPricingSubTab('PLC');
        localStorage.setItem('secondaryTab', 'PLC');
      } else {
        setSecondaryTab(savedSecondaryTab);
      }
    }
  }, [location.key, location.state]);

  useEffect(() => {
    const clearStorage = () => {
      localStorage.removeItem('secondaryTab');
    };

    // Clear on unmount
    return () => {
      clearStorage();
    };
  }, []);
  /* 
    Event handlers
  */
  const handleChangeSecondaryTab = useCallback((tabName) => {
    setSecondaryTab(tabName);
    localStorage.setItem('secondaryTab', tabName); // Save to localStorage
  }, []);

  const handleToggleOpenBlockDetailingModal = () => {
    setIsOpenBlockDetailingModal(!isOpenBlockDetailingModal);
  };

  const handleToggleOpenUnitDetailingModal = () => {
    setIsOpenUnitDetailingModal(!isOpenUnitDetailingModal);
  };

  const handleToggleOnClose = () => {
    setOpenAccommodation(!OpenAccommodation);
  };

  const handleToggleCreateNewModal = () => {
    setOpenAccommodation(!OpenAccommodation);
  };

  const handleToggleImportAccommodationModal = () => {
    setisOpenImportAccommodation(!isOpenImportAccommodation);
  };

  const handleToggleImportAccommodationModalOnClose = () => {
    setisOpenImportAccommodation(!isOpenImportAccommodation);
  };

  const handleToggleAddDetailsModal = useCallback(() => {
    setIsOpenAddDetailsModal(!isOpenAddDetailsModal);
  }, [isOpenAddDetailsModal]);

  const ShowValue = (data) => {
    // console.log('hey data', data);
    setIsShow(data);
  };

  const handleOnEdit = (id) => {
    // console.log('This is the id', id);
    setStaticId(id);
    setIsOpenEditAccommodation(!isOpenEditAccommodation);
  };

  const { mutate: handleEditProject, isLoading: isEditingProject } = useMutation({
    mutationFn: (updatedProject) => {
      const parsedLandId =
        updatedProject?.land_id !== null && updatedProject?.land_id !== undefined
          ? parseInt(updatedProject.land_id, 10)
          : null;
      return editProject(projectId, {
        ...updatedProject,
        land_id: parsedLandId
      });
    },
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async (result) => {
      setIsSubmitting(false);
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        toast.error(result?.response?.data?.message || result?.message || 'Error editing project');
        return;
      }
      toast.success('Project edited successfully');
      setIsOpenEditProject(false);
      await refetchProject();
    },
    onError: (err) => {
      setIsSubmitting(false);
      toast.error(err?.response?.data?.message || err?.message || 'Error editing project');
    }
  });

  const { mutate: runProjectDeleteCheck } = useMutation({
    mutationFn: (id) => handleCheckProjectDelete(id),
    onMutate: () => {
      setIsProjectDeleteBusy(true);
      mixpanel.track('Check Project Delete Dependencies Started', {
        user_id: userId,
        company_id: companyIdInt,
        project_id: projectId,
        timestamp: new Date().toISOString()
      });
    },
    onSuccess: async (result, id) => {
      setIsProjectDeleteBusy(false);
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        console.error('Error checking dependencies', result);
        toast.error(
          result?.response?.data?.message || result?.message || 'Error checking dependencies'
        );
        return;
      }

      const projectName = project?.name || 'Project';

      if (result.canDelete) {
        setCanDelete('yes');
        setResData({ name: projectName });
        setSelectedIds([parseInt(id, 10)]);
      } else {
        setResData({ ...result, name: projectName });
        setCanDelete('no');
      }
    },
    onError: (err) => {
      setIsProjectDeleteBusy(false);
      console.error('Error checking dependencies', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error checking dependencies');
    }
  });

  const { mutate: handleDeleteFn } = useMutation({
    mutationFn: (payload) => deleteProject(payload),
    onMutate: () => {
      setIsProjectDeleteBusy(true);
      mixpanel.track('Delete Project Started', {
        user_id: userId,
        company_id: companyIdInt,
        project_ids: projectId,
        timestamp: new Date().toISOString()
      });
    },
    onSuccess: (result, variables) => {
      setIsProjectDeleteBusy(false);
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        console.error('Error deleting project', result);
        toast.error(result?.response?.data?.message || result?.message || 'Error deleting project');
        return;
      }

      toast.success('Project Deleted Successfully');
      refetchProjects();
      setCanDelete(null);
      setSelectedIds([]);

      const navCompanyId = project?.company_id ?? project?.company?.id;
      if (navCompanyId != null && navCompanyId !== '') {
        navigate(`/builder/management/company/${navCompanyId}/projects`);
      } else {
        navigate(-1);
      }

      mixpanel.track('Delete Project Success', {
        user_id: userId,
        company_id: companyIdInt,
        project_ids: variables?.ids,
        timestamp: new Date().toISOString()
      });
    },
    onError: (err, variables) => {
      setIsProjectDeleteBusy(false);
      console.error('Error deleting project', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error deleting project');

      mixpanel.track('Delete Project Failed', {
        user_id: userId,
        company_id: companyIdInt,
        project_ids: variables?.ids,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const handleRequestProjectDelete = useCallback(() => {
    if (!projectId) return;
    runProjectDeleteCheck(parseInt(projectId, 10));
  }, [projectId, runProjectDeleteCheck]);

  const handleConfirmProjectDelete = useCallback(
    (ids) => {
      mixpanel.track('Delete Project Button Clicked', {
        user_id: userId,
        company_id: companyIdInt,
        project_ids: ids,
        timestamp: new Date().toISOString()
      });
      handleDeleteFn({ ids });
    },
    [userId, companyIdInt, handleDeleteFn]
  );

  /* 
    Effects
  */
  useEffect(() => {
    if (isOpenAddDetailsModal) {
      // console.log('Preventing scroll');
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpenAddDetailsModal]);

  useEffect(() => {
    refetchProject();
  }, [refetchProject, projectId]);

  /* 
    JSX
  */
  return (
    <>
      <Loader isVisible={isProjectLoading || isProjectFetching} />
      {isOpenImportRera && (
        <ImportReraModal
          projectId={projectId}
          onClose={() => setIsOpenImportRera(false)}
          onImported={() => refetchProject()}
        />
      )}
      {isOpenImportAccommodation && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Import Company Accomodation Details'}
          onClose={handleToggleImportAccommodationModalOnClose}
          width={60}>
          <div className="h-[17rem]">
            <ImportAccommodation />
          </div>
        </Modal>
      )}
      {OpenAccommodation && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={`Add Accommodation - ${project?.name}`}
          onClose={handleToggleOnClose}
          width={72}>
          <AddAccommodation
            onSubmitParking={handleOnSubmitParking}
            onSubmitGroup={handleOnSubmitGroup}
            isSubmitting={isSubmitting}
            onSubmitIndependent={handleOnSubmitIndependent}
            independentData={AddIndependentStaticState}
            groupData={AddGroupStaticState}
            parkingData={AddParkingStaticState}
            ShowValue={ShowValue}
            onClose={handleToggleOnClose}
          />
        </Modal>
      )}
      {isOpenEditAccommodation && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={`Edit Accommodation - ${project?.name}`}
          onClose={() => setIsOpenEditAccommodation(false)}
          width={72}>
          <EditAccommodation
            onEditIndependent={handleOnEditSubmitIndependent}
            onEditParking={handleOnEditSubmitParking}
            isSubmitting={isSubmitting}
            onEditGroup={handleOnEditSubmitGroup}
            id={StaticId}
            accommodationData={accommodations}
            onClose={() => setIsOpenEditAccommodation(false)}
          />
        </Modal>
      )}
      {isOpenEditProject && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={88}
          name="Edit project"
          onClose={handleToggleEditProjectModal}
          upShift={upShift * 10}>
          <EditProject
            id={projectId}
            upShift={upShift}
            isSubmitting={isSubmitting || isEditingProject}
            setUpShift={setUpShift}
            onSubmit={handleEditProject}
            onClose={handleToggleEditProjectModal}
          />
        </Modal>
      )}

      {canDelete === 'no' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={`Cannot delete '${resData?.name || 'this project'}'`}
          onClose={() => setCanDelete(null)}
          width={45}>
          <div className="flex flex-col justify-center align-center ">
            <p className="text-center">
              Looks like this project has dependencies. Please resolve the following items to
              proceed.
            </p>
            <div className="flex justify-start items-center flex-wrap bg-[#f5f7f8] p-2 rounded-[5px] mt-4">
              {resData &&
                resData.reason?.map((r, index) => (
                  <span className="mt-[2px] mr-[4px] text-[#535353]" key={index}>
                    {index + 1}.{r}
                    {index < resData.reason.length - 1 ? ', ' : ''}
                  </span>
                ))}
            </div>
          </div>
        </Modal>
      )}

      {canDelete === 'yes' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={'Confirmation'}
          onClose={() => {
            setCanDelete(null);
            setSelectedIds([]);
          }}
          width={45}>
          <DeleteConfirmationModal
            name={resData.name}
            title={'project'}
            onClose={() => setCanDelete(null)}
            data={projectsForDeleteModal}
            type={'project'}
            setCanDelete={setCanDelete}
            onDeleteClick={handleConfirmProjectDelete}
            setSelectedIds={setSelectedIds}
            selectedIds={selectedIds}
          />
        </Modal>
      )}

      {/* Modals */}
      {isOpenAddDetailsModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={72}
          name="Add details"
          onClose={handleToggleAddDetailsModal}>
          <div className="flex justify-between items-center p-[10rem] space-x-12">
            <div
              className="w-[50%] p-4 py-8 bg-white shadow-lg flex flex-col justify-center items-center gap-4 rounded-xl cursor-pointer"
              onClick={() => setIsOpenBlockDetailingModal(true)}>
              <span className="font-semibold text-xl">Block Details</span>
              <span>Based on Blocks</span>
            </div>

            <div
              className="w-[50%] p-4 py-8 bg-white shadow-lg flex flex-col justify-center items-center gap-4 rounded-xl cursor-pointer"
              onClick={() => setIsOpenUnitDetailingModal(true)}>
              <span className="font-semibold text-xl">Unit Details</span>
              <span>Based on Units</span>
            </div>
          </div>
        </Modal>
      )}

      {isOpenBlockDetailingModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={72}
          name="Block Detailing"
          onClose={handleToggleOpenBlockDetailingModal}
          upShift={16}>
          <PreviewBlockDetails />
        </Modal>
      )}

      {isOpenUnitDetailingModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={72}
          name="Unit Detailing"
          onClose={handleToggleOpenUnitDetailingModal}
          upShift={16}>
          <PreviewUnitDetails />
        </Modal>
      )}

      {/* Main content */}
      <Layout>
        {/* Header */}
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header
            data={{ name: `Company > Projects > ${project?.name}` }}
            backArrow={true}
            onBack={onBack}
          />
        </div>

        <div className={` relative mt-[4rem]`}>
          {/* Compact Combined Tab Navigation */}
          <Headroom
            upTolerance={0}
            downTolerance={10}
            offset={10}
            style={{
              transition: 'all 0.2s ease-in-out',
              position: 'fixed',
              top: '4rem',
              left: collapsed ? '76px' : '16.6%',
              right: '0',
              zIndex: 2
            }}>
            <div className="bg-white border-b border-gray-200 w-full">
              <div className="flex justify-between items-center pl-4 py-3">
                {/* All Tabs */}
                <div className="flex items-center space-x-0.5">
                  {secondaryTabbedComponentMetaData.tabs.map((item, index) => (
                    <div
                      key={index}
                      className={`px-3 py-1 text-sm cursor-pointer transition-all duration-200 rounded-md font-semibold ${
                        item === secondaryTab
                          ? 'bg-primary text-white font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      onClick={() => handleChangeSecondaryTab(item)}>
                      {item}
                    </div>
                  ))}
                </div>
                <div className="pr-4">
                  <Button variant="secondary" size="sm" onClick={() => setIsOpenImportRera(true)}>
                    Import from RERA
                  </Button>
                </div>
              </div>
            </div>
          </Headroom>

          {/* Content sections - no more secondary tab component needed */}
          {/* Overview content */}
          {secondaryTab === 'Overview' && (
            <div className="w-full h-full">
              <ProjectOverview
                onEditProject={handleToggleEditProjectModal}
                onDeleteProject={handleRequestProjectDelete}
                isDeleteSubmitting={isProjectDeleteBusy}
              />
            </div>
          )}

          {/* Accommodation content */}
          {secondaryTab === 'Accommodation' && (
            <div className="w-full h-full">
              <AccommodationDetails
                accommodations={accommodations}
                isTableLoading={isAccommodationsLoading}
                isTableRefreshing={isAccommodationsFetching && !isAccommodationsLoading}
                isTableError={isAccommodationsError}
                tableErrorMessage={
                  accommodationsError?.response?.data?.message ||
                  accommodationsError?.message ||
                  'Failed to load accommodations.'
                }
                onEdit={handleOnEdit}
                refresh={refetchAccommodations}
                handleToggleCreateNewModal={handleToggleCreateNewModal}
              />
            </div>
          )}

          {secondaryTab === 'Management Task' && (
            <div className="w-full min-h-full bg-gray-100">
              <ProjectManagementTasks projectId={projectId} />
            </div>
          )}

          {secondaryTab === 'Block' && (
            <div className="w-full h-full">
              <BlockDetails projectId={projectId} />
            </div>
          )}

          {secondaryTab === 'Pricing' && (
            <div className="w-full min-h-full bg-gray-100">
              {/* Second-level tabs for Pricing */}
              <div className="bg-white border-b border-gray-200">
                <div className="px-4 py-3 w-full flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-1 p-1.5 bg-gray-100 rounded-full w-fit border border-gray-200 shadow-md">
                    {pricingSubTabs.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        className={`px-3 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                          pricingSubTab === tab
                            ? 'bg-primary text-white'
                            : 'text-gray-600 hover:bg-gray-200'
                        }`}
                        onClick={() => {
                          setPricingSubTab(tab);
                          localStorage.setItem('secondaryTab', tab);
                        }}>
                        {tab}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2 flex-shrink-0 min-h-[2.5rem]">
                    {pricingSubTab === 'Payment Schedule' && (
                      <div className="flex flex-wrap items-center gap-1 p-1.5 bg-slate-50 rounded-full w-fit border border-gray-200 shadow-sm">
                        {paymentScheduleSubTabs.map((tab) => (
                          <button
                            key={tab}
                            type="button"
                            className={`px-3 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                              paymentScheduleSubTab === tab
                                ? 'bg-primary text-white'
                                : 'text-gray-600 hover:bg-gray-200'
                            }`}
                            onClick={() => {
                              setPaymentScheduleSubTab(tab);
                              localStorage.setItem('pricingPaymentScheduleSubTab', tab);
                            }}>
                            {tab}
                          </button>
                        ))}
                      </div>
                    )}
                    {pricingSubTab === 'Unit Pricing' && (
                      <div ref={unitPricingBookPortalRef} className="flex items-center" />
                    )}
                    {pricingSubTab === 'Accommodation Pricing' && (
                      <div
                        ref={accommodationPricingFilterPortalRef}
                        className="flex flex-wrap items-center justify-end gap-2"
                      />
                    )}
                  </div>
                </div>
              </div>
              {/* Pricing sub-tab content */}
              {pricingSubTab === 'Unit Pricing' && (
                <div className="w-full min-h-full">
                  <UnitPricingList
                    projectName={project?.name}
                    bookPortalEl={unitPricingBookPortalEl}
                    anchorBookInPricingSubtab
                  />
                </div>
              )}
              {pricingSubTab === 'Accommodation Pricing' && (
                <div className="w-full min-h-full">
                  <RateList
                    projectName={project?.name}
                    filterPortalEl={accommodationPricingFilterPortalEl}
                    anchorFiltersInPricingSubtab
                  />
                </div>
              )}
              {pricingSubTab === 'PLC' && (
                <div className="w-full min-h-full">
                  <PLCsDetails />
                </div>
              )}
              {pricingSubTab === 'Extra Charges' && (
                <div className="w-full min-h-full">
                  <ExtraChargesList projectName={project?.name} />
                </div>
              )}
              {pricingSubTab === 'Directions to Units' && (
                <div className="w-full min-h-full">
                  <DirectionsToUnits />
                </div>
              )}
              {pricingSubTab === 'Payment Schedule' && (
                <div className="w-full min-h-full">
                  <PaymentStagesDetails
                    activeView={paymentScheduleSubTab}
                    projectId={projectId}
                  />
                </div>
              )}
            </div>
          )}

          {secondaryTab === 'Layout' && (
            <div className="w-full min-h-full bg-gray-100">
              <ProjectLayoutTab projectId={projectId} />
            </div>
          )}

          {secondaryTab === 'Property Templates' && (
            <div className="w-full min-h-full bg-gray-100">
              <PropertyDocuments />
            </div>
          )}

          {secondaryTab === 'Project Documents' && (
            <div className="w-full min-h-full bg-gray-100">
              <ProjectDocument />
            </div>
          )}
        </div>
      </Layout>
    </>
  );
};

export default ProjectDetails;
